import { setUser } from '@sentry/react';
import { useEffect, useState } from 'react';

import { useIsImpersonating, useUser } from '~/api/user';
import { useColorThemeContext } from '~/providers/ColorThemeProvider/ColorThemeProvider.context';
import { useLanguageContext } from '~/providers/LanguageProvider/LanguageProvider.context';

/** Set the app color theme to the user's preferred color theme */
export const useUserColorTheme = () => {
  const { data: user } = useUser();
  const { setColorTheme } = useColorThemeContext();

  useEffect(() => {
    setColorTheme((previous) => user.appSettings.theme?.appColor ?? previous);
  }, [setColorTheme, user.appSettings.theme?.appColor]);
};

/** Set the app language to the user's preferred language */
export const useUserLanguage = () => {
  const { data: user } = useUser();
  const { setLanguage } = useLanguageContext();

  useEffect(() => {
    setLanguage(user.lang);
  }, [setLanguage, user.lang]);
};

/** Update Sentry user context with the authenticated user's information */
export const useIdentifyUserInSentry = () => {
  const { data: user } = useUser();
  const isImpersonating = useIsImpersonating();

  useEffect(() => {
    const fullName = `${user.firstName} ${user.lastName}`;
    const name = isImpersonating ? `Admin – ${fullName}` : fullName;

    setUser({
      id: user.id,
      username: name,
      email: user.email,
      ip_address: '{{auto}}',
    });
  }, [isImpersonating, user.email, user.firstName, user.id, user.lastName]);
};

/**
 * Reload the page when the session switches to another user, e.g. an admin impersonating someone else in another tab.
 * The session cookie and the broadcast query cache are shared between tabs, so without a reload this tab would mix
 * the previous user's data with the new user's. Returns whether a switch is pending, so the page can stop rendering
 * the previous user's data until the reload lands.
 */
export const useReloadOnUserSwitch = () => {
  const { data: user } = useUser();
  const [initialUserId] = useState(user.id);
  const hasSwitchedUser = user.id !== initialUserId;

  useEffect(() => {
    if (hasSwitchedUser) window.location.reload();
  }, [hasSwitchedUser]);

  return hasSwitchedUser;
};
